import React, { useEffect, useRef, useState } from 'react';
import './Chatbot.css';
import {
  FiMessageCircle,
  FiX,
  FiSend,
  FiMinus,
  FiPhone,
  FiMail,
  FiExternalLink,
} from 'react-icons/fi';
import { answerLocal } from './chatbotNlu';

const QUICK_REPLIES = [
  'What services do you offer?',
  'Show me your projects',
  'How long does a project take?',
  'What does it cost?',
  'How do I contact you?',
];

const API_ENDPOINT = '/api/chat';
const MAX_HISTORY = 10;

const initialMessages = [
  {
    from: 'bot',
    text: "Hi 👋 I'm the THINKINGWORLD assistant — the studio founded by Barani. Ask me anything about our services, industries, pricing or how to start a project — I'll do my best to help.",
  },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

const buildHistory = (messages, latestUserText) => {
  const history = messages
    .filter((m) => m.text && (m.from === 'user' || m.from === 'bot'))
    .slice(-MAX_HISTORY)
    .map((m) => ({
      role: m.from === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));
  history.push({ role: 'user', content: latestUserText });
  return history;
};

const detectActionsFromReply = (text) => {
  const actions = [];
  const lower = text.toLowerCase();
  if (lower.includes('contact form') || lower.includes('contact page')) {
    actions.push({ label: 'Open contact form', scrollTo: 'contact' });
  }
  if (lower.includes('medical-appointment-system') || lower.includes('live site')) {
    actions.push({ label: 'Visit live site', href: 'https://medical-appointment-system-4h6x.vercel.app/', external: true });
  }
  if (lower.includes('thinkingworld813@gmail.com') && !actions.some((a) => a.href === 'mailto:thinkingworld813@gmail.com')) {
    actions.push({ label: 'Email us', href: 'mailto:thinkingworld813@gmail.com' });
  }
  if (lower.includes('+91 95979') || lower.includes('whatsapp')) {
    actions.push({ label: 'Call now', href: 'tel:+919597965911' });
  }
  return actions;
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true); // optimistic
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  useEffect(() => {
    if (open) setHasUnread(false);
  }, [open]);

  const callApi = async (history) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) {
        if (res.status === 503 || res.status === 404) {
          return { ok: false, disable: true };
        }
        return { ok: false };
      }
      const data = await res.json();
      if (!data.reply) return { ok: false };
      return { ok: true, reply: data.reply };
    } catch (e) {
      clearTimeout(timeout);
      return { ok: false, disable: e.name === 'TypeError' };
    }
  };

  const send = async (rawText) => {
    const text = rawText.trim();
    if (!text) return;

    const userMsg = { from: 'user', text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setTyping(true);

    let replied = false;

    if (apiAvailable) {
      const history = buildHistory(messages, text);
      const result = await callApi(history);
      if (result.ok) {
        const actions = detectActionsFromReply(result.reply);
        setMessages((m) => [...m, { from: 'bot', text: result.reply, actions }]);
        replied = true;
      } else if (result.disable) {
        setApiAvailable(false);
      }
    }

    if (!replied) {
      // graceful local NLU fallback
      const local = answerLocal(text);
      setMessages((m) => [...m, { from: 'bot', text: local.reply, actions: local.actions }]);
    }

    setTyping(false);
  };

  const handleAction = (action) => {
    if (action.scrollTo) {
      scrollToSection(action.scrollTo);
      setOpen(false);
    } else if (action.href) {
      if (action.external) {
        window.open(action.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = action.href;
      }
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  return (
    <>
      <button
        className={`chatbot-fab ${open ? 'chatbot-fab--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <FiX /> : <FiMessageCircle />}
        {!open && hasUnread && <span className="chatbot-fab-pulse" />}
      </button>

      <div className={`chatbot-panel ${open ? 'chatbot-panel--open' : ''}`}>
        <header className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar">
              <img src="/img/company-logo.png" alt="THINKINGWORLD" />
            </div>
            <div>
              <strong>THINKINGWORLD Assistant</strong>
              <span><span className="chatbot-status-dot" /> Online · replies in seconds</span>
            </div>
          </div>
          <button className="chatbot-min" onClick={() => setOpen(false)} aria-label="Minimize">
            <FiMinus />
          </button>
        </header>

        <div className="chatbot-body" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={`chatbot-msg chatbot-msg--${m.from}`}>
              {m.from === 'bot' && (
                <div className="chatbot-msg-avatar">
                  <img src="/img/company-logo.png" alt="" />
                </div>
              )}
              <div className="chatbot-msg-bubble">
                {m.text.split('\n').map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
                {m.actions && m.actions.length > 0 && (
                  <div className="chatbot-msg-actions">
                    {m.actions.map((a, idx) => (
                      <button key={idx} onClick={() => handleAction(a)} className="chatbot-action-btn">
                        {a.label}
                        {a.external ? <FiExternalLink /> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {typing && (
            <div className="chatbot-msg chatbot-msg--bot">
              <div className="chatbot-msg-avatar">
                <img src="/img/company-logo.png" alt="" />
              </div>
              <div className="chatbot-msg-bubble chatbot-typing">
                <span /><span /><span />
              </div>
            </div>
          )}

          {messages.length <= 1 && !typing && (
            <div className="chatbot-quick">
              <span className="chatbot-quick-label">Quick questions</span>
              <div className="chatbot-quick-row">
                {QUICK_REPLIES.map((q) => (
                  <button key={q} onClick={() => send(q)}>{q}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="chatbot-shortcut">
          <a href="tel:+919597965911" className="chatbot-shortcut-btn">
            <FiPhone /> Call
          </a>
          <a href="mailto:thinkingworld813@gmail.com" className="chatbot-shortcut-btn">
            <FiMail /> Email
          </a>
        </div>

        <form className="chatbot-input" onSubmit={onSubmit}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your question…"
            aria-label="Type your message"
            disabled={typing}
          />
          <button type="submit" disabled={!input.trim() || typing} aria-label="Send">
            <FiSend />
          </button>
        </form>
      </div>
    </>
  );
};

export default Chatbot;
